import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  ListItemIcon,
  Divider,
  InputLabel,
  FormControl,
  Select,
  TablePagination,
  useMediaQuery,
  Chip,
  Tooltip,
} from "@mui/material";

import { Helmet } from "react-helmet-async";
import {
  Delete,
  SwapHoriz,
  SwapVert,
  Close,
  IosShare as IosShareIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import CaseLawPreviewDialog from "../../dialogs/CaseLawPreviewDialog";
import StatutePreviewDialog from "../../dialogs/StatutePreviewDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRectangleList,
  faBarsStaggered,
} from "@fortawesome/free-solid-svg-icons";
import {
  Search,
  MoreHorizontal,
  Eye,
  FileText,
  Scale,
  Calendar,
  Edit3,
  Trash2,
  Copy,
  BookOpen,
  Gavel,
  ChevronUp,
  ChevronDown,
  Filter,
  Bookmark,
  BarChart3,
  ArrowRightLeft,
  ShareIcon,
  Link2,
} from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";

import {
  copyBookmark,
  removeNote,
  updateNote,
  fetchBookmarkCases,
  fetchBookmarkStatutes,
  fetchBookmarkCmsDocs,
} from "../../store/Bookmarks/bookmarksActions";
import {
  DELETE_BOOKMARK_CASE,
  DELETE_BOOKMARK_FROM_GROUP,
  DELETE_BOOKMARK_STATUTE,
  DELETE_BOOKMARK_CMS_DOC,
} from "../../store/Bookmarks/bookmarksTypes";
import { removeCmsDocFromGroupApi } from "../../services/api/bookmarksApi";
import CaseDetailDialog from "../../dialogs/CaseDetailDialog";
import { findCaseSummary } from "../../actions/searchActions";
import CaseSummaryDialog from "../../dialogs/CaseSummaryDialog";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import { Stack } from "@mui/system";
import StatuteDetailDialog from "../../dialogs/StatuteDetailDialog";
import { searchSections } from "../../actions/statuteSearchActions";
import { removeItemFromGroupApi } from "../../services/api/bookmarksApi";
import { handleShare } from "../../utils/HelperFunction/utils";
import { toast } from "react-toastify";
import MoveBookmarkDialog from "../../dialogs/MoveBookmarkDialog";
import EditNoteDialog from "../../dialogs/EditNoteDialog";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import LogoLoader from "../UI/LogoLoader";

const BookmarksTable = ({ toggleSidebar, selectedGroup }) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const bookmarkGroups = useSelector((state) => state.bookmarkGroups);
  const { groups } = bookmarkGroups;

  const bookmarkCases = useSelector((state) => state.bookmarkCases);
  const { loadingCases, cases } = bookmarkCases;

  const bookmarkStatutes = useSelector((state) => state.bookmarkStatutes);
  const { loadingStatutes, statutes } = bookmarkStatutes;

  const bookmarkCmsDocs = useSelector((state) => state.bookmarkCmsDocs);
  const { loadingCmsDocs, cmsDocs } = bookmarkCmsDocs;

  const [searchTerm, setSearchTerm] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [openStatuteSectionPreview, setOpenStatuteSectionPreview] =
    useState(false);
  const [openSummary, setOpenSummary] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedStatute, setSelectedStatute] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [targetGroupId, setTargetGroupId] = useState("");
  const [editNoteItem, setEditNoteItem] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCaseInsights, setOpenCaseInsights] = useState(false);
  const [selectItem, setSelectItem] = useState([]);
  const [bookmarkAlreadyExists, setBookmarkAlreadyExists] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);

  const currentGroupIdRef = useRef(null);

  // Fetch data when selectedGroup changes
  useEffect(() => {
    if (selectedGroup?.id && groups) {
      const group = groups.find((g) => g._id === selectedGroup.id);
      if (group) {
        if (currentGroupIdRef.current !== selectedGroup.id) {
          currentGroupIdRef.current = selectedGroup.id;
          setPage(0);
          setSearchTerm("");
          setSelectedCategory("all");
          setSortConfig({ key: "date", direction: "desc" });
        }
        dispatch(fetchBookmarkCases(group));
        dispatch(fetchBookmarkStatutes(group));
        dispatch(fetchBookmarkCmsDocs(group));
      }
    }
  }, [selectedGroup?.id, groups, dispatch]);

  const handlePreviewClick = (item) => {
    if (item.type === "cmsDoc") {
      if (item.fileUrl) window.open(item.fileUrl, "_blank");
      return;
    }

    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    // Validate item before processing
    if (!item || !item.type) {
      console.error("Invalid bookmark item:", item);
      return;
    }

    try {
      if (item.type === "case") {
        if (!item.Title && !item.Bookmark_ID) {
          console.error("Invalid case bookmark data:", item);
          return;
        }
        setSelectedCase(item);
        setOpenPreview(true);
      } else if (item.type === "statute") {
        if (!item.Statute_Name && !item.Bookmark_ID) {
          console.error("Invalid statute bookmark data:", item);
          return;
        }
        setSelectedStatute(item);
        const cleanedName = item?.Statute_Name;
        dispatch(searchSections("", cleanedName, "Title"));
        setOpenPreview(true);
      } else if (item.type === "section") {
        if (!item.Statute_Name && !item.Section && !item.Bookmark_ID) {
          console.error("Invalid section bookmark data:", item);
          return;
        }
        setSelectedStatute(item);
        setOpenStatuteSectionPreview(true);
      }

      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Preview Bookmarked Document" },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error in handlePreviewClick:", error);
      // Show user feedback for preview errors
      setSnackbarMessage({
        type: "error",
        text: "Failed to preview bookmark. Please try again.",
      });
    }
  };

  const handleSummaryClick = async (caseItem) => {
    setSelectedCase(caseItem);
    setOpenSummary(true);
  };

  const handleNoteClick = (item) => {
    handleEditNote(item);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedCase(null);
    setSelectedStatute(null);
  };

  const handleOpenConfirmDialog = (itemId, groupId, itemType) => {
    setItemToDelete({ itemId, groupId, itemType });
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setItemToDelete(null);
  };

  const handleCategoryChange = (value) => {
    setPage(0);
    setSelectedCategory(value);
  };

  const handleSearchChange = (event) => {
    setPage(0);
    setSearchTerm(event.target.value);
  };

  const handleDelete = async (itemId, groupId, itemType) => {
    try {
      if (itemType === "cmsDoc") {
        const response = await removeCmsDocFromGroupApi(
          userInfo._id,
          groupId,
          itemId,
        );
        if (response.status === 200) {
          dispatch({ type: DELETE_BOOKMARK_CMS_DOC, payload: { itemId } });
          dispatch({
            type: DELETE_BOOKMARK_FROM_GROUP,
            payload: { groupId, itemId, itemType },
          });
          setSnackbarMessage({ type: "success", text: "Bookmark Deleted!" });
        }
        return;
      }
      const caseId = itemType === "case" ? itemId : undefined;
      const statuteId =
        itemType === "statute" || itemType === "section" ? itemId : undefined;
      const response = await removeItemFromGroupApi(
        userInfo._id,
        groupId,
        caseId,
        statuteId,
      );

      if (response.status === 200) {
        if (itemType === "case") {
          dispatch({ type: DELETE_BOOKMARK_CASE, payload: { itemId } });
          //setCases(cases.filter((caseItem) => caseItem.id !== itemId));
        } else if (itemType === "statute" || itemType === "section") {
          dispatch({ type: DELETE_BOOKMARK_STATUTE, payload: { itemId } });
        }
        dispatch({
          type: DELETE_BOOKMARK_FROM_GROUP,
          payload: { groupId, itemId, itemType },
        });
        setSnackbarMessage({
          type: "success",
          text: "Bookmark Deleted!",
        });
      } else {
        console.error("Error deleting item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handleMove = (item) => {
    setSelectedItem(item);
    setMoveModalOpen(true);
  };

  const handleMoveConfirm = async () => {
    if (!targetGroupId) return;
    try {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
        return;
      }
      const result = await dispatch(
        copyBookmark({
          userId: userInfo._id,
          fromGroupId: selectedGroup?.id,
          toGroupId: targetGroupId,
          caseId:
            selectedItem.type === "case" ? selectedItem.Bookmark_ID : undefined,
          statuteId:
            selectedItem.type === "statute" || selectedItem.type === "section"
              ? selectedItem.Bookmark_ID
              : undefined,
          selectedItem,
        }),
      );
      if (result?.success) {
        setSnackbarMessage({
          type: "success",
          text: result.message,
        });
        setMoveModalOpen(false);
        setSelectedItem(null);
        setTargetGroupId("");
      } else {
        setSnackbarMessage({
          type: "error",
          text: "Failed to copy the bookmark.",
        });
      }
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Move bookmark item to another group",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error moving item:", error);
    }
  };

  const handleSaveNote = (note) => {
    if (!editNoteItem || !note) return;
    try {
      dispatch(
        updateNote({
          userId: userInfo._id,
          groupId: selectedGroup?.id,
          itemId: editNoteItem.Bookmark_ID,
          itemType: editNoteItem.type,
          note,
        }),
      );
      setEditNoteItem(null);
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const handleRemoveNoteClick = () => {
    try {
      dispatch(
        removeNote({
          userId: userInfo._id,
          groupId: selectedGroup?.id,
          itemId: editNoteItem.Bookmark_ID,
          itemType: editNoteItem.type,
        }),
      );

      setEditNoteItem(null);
    } catch (error) {
      console.error("Error removing note:", error);
    }
  };

  const handleEditNote = (item) => {
    setEditNoteItem(item);
  };

  const handleSelectGroupToCopy = (targetGroupId) => {
    setTargetGroupId(targetGroupId);

    const targetGroup = groups?.find((group) => group._id === targetGroupId);

    const collection =
      selectItem.type === "case" ? targetGroup.cases : targetGroup.statutes;

    const itemExists = collection?.some(
      (item) => item.id === selectItem.Bookmark_ID,
    );
    setBookmarkAlreadyExists(itemExists);
  };

  const handleMoveCancel = () => {
    setMoveModalOpen(false);
    setSelectedItem(null);
    setTargetGroupId("");
    setBookmarkAlreadyExists(false);
  };

  const formatCaseTitle = (title) => {
    if (!title) return;
    const splitTitle = title?.split("Honorable")[0];
    const titleCaseTitle = splitTitle
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const hasData = cases.length > 0 || statutes.length > 0 || cmsDocs.length > 0;

  const sortedItems = [...cases, ...statutes, ...cmsDocs]
    .filter((item) => {
      if (
        item.type === "case" &&
        (selectedCategory === "all" || selectedCategory === "case")
      ) {
        return item.Title.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (
        (item.type === "statute" || item.type === "section") &&
        (selectedCategory === "all" || selectedCategory === "statute")
      ) {
        return item.Statute_Name.toLowerCase().includes(
          searchTerm.toLowerCase(),
        );
      } else if (
        item.type === "cmsDoc" &&
        (selectedCategory === "all" || selectedCategory === "cmsDoc")
      ) {
        return (item.title || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      }
      return false;
    })
    .sort((a, b) => {
      const isAsc = sortConfig.direction === "asc";
      if (sortConfig.key === "title") {
        const titleA =
          a.type === "case"
            ? a.Title.toLowerCase()
            : a.type === "cmsDoc"
              ? (a.title || "").toLowerCase()
              : a.Statute_Name.toLowerCase();
        const titleB =
          b.type === "case"
            ? b.Title.toLowerCase()
            : b.type === "cmsDoc"
              ? (b.title || "").toLowerCase()
              : b.Statute_Name.toLowerCase();
        return isAsc
          ? titleA.localeCompare(titleB)
          : titleB.localeCompare(titleA);
      } else if (sortConfig.key === "date") {
        const dateA = new Date(a.addedAt);
        const dateB = new Date(b.addedAt);
        return isAsc ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });

  const sortedAndPaginatedResult =
    sortedItems.length > 0 ? sortedItems.slice(startIndex, endIndex) : [];

  const isGroupChanging =
    !!selectedGroup?.id && currentGroupIdRef.current !== selectedGroup?.id;

  return (
    <>
      <Helmet>
        <title>LawGPT - Bookmarks</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
          flex: 1,
          padding: { xs: "16px", lg: "24px" },
          margin: "12px",
          borderRadius: "12px",
          border: "1px solid #F1F5F9",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "auto",
          fontFamily:
            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "#F8FAFC",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "8px",
            "&:hover": {
              background: "#94A3B8",
            },
          },
        }}
      >
        <>
          {/* Header with Hamburger Menu - Always Visible */}
          <Stack direction="row" mb={isMobileView ? 1 : 0}>
            <IconButton
              size="small"
              sx={{ display: { xs: "flex", lg: "none" } }}
              onClick={toggleSidebar}
            >
              <FontAwesomeIcon icon={faBarsStaggered} fontSize="medium" />
            </IconButton>
            <Typography
              sx={{
                fontSize: { xs: "14px", lg: "18px" },
                mb: 1,
                ml: 1,
                mt: 1,
                color: "#3C50B1",
                fontWeight: 600,
              }}
            >
              {selectedGroup?.name}
            </Typography>
          </Stack>

          {/* Search and Filter Controls - Always Visible */}
          <Stack
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: { xs: 1, lg: 0 },
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={{
                width: "100%",
                maxWidth: "400px",
                mt: { xs: 0, lg: 1 },
                mb: { xs: 0, lg: 2 },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#00509e",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00509e",
                  },
                },
                "& input": {
                  padding: "10px",
                  fontSize: "13px",
                },
              }}
              placeholder="Search bookmarks..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "#303f9f" }} fontSize="small" />
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0.5, lg: 1 },
                alignItems: "center",
              }}
            >
              <FormControl
                variant="outlined"
                sx={{
                  width: 110,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    height: { xs: "36px", lg: "30px" },
                    padding: "8px",
                  },
                  "& fieldset": {
                    borderColor: "#ddd",
                    borderWidth: "1px",
                  },
                  "& .MuiSelect-icon": {
                    right: "4px",
                  },
                  "& .MuiSelect-select": {
                    padding: "0px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <Select
                  id="filterByCategory"
                  value={selectedCategory}
                  onChange={(e) => {
                    setPage(0);
                    handleCategoryChange(e.target.value);
                  }}
                  sx={{
                    "&.MuiOutlinedInput-root": {
                      padding: "0px",
                    },
                    "& .MuiSelect-select": {
                      padding: "0px 8px",
                      fontSize: { xs: "12px", md: "14px" },
                    },
                  }}
                  displayEmpty
                >
                  <MenuItem
                    value="all"
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                  >
                    All
                  </MenuItem>
                  <MenuItem
                    value="case"
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                  >
                    Cases
                  </MenuItem>
                  <MenuItem
                    value="statute"
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                  >
                    Statutes
                  </MenuItem>
                  <MenuItem
                    value="section"
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                  >
                    Sections
                  </MenuItem>
                  <MenuItem
                    value="cmsDoc"
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                  >
                    CMS Docs
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {isGroupChanging ||
          loadingCases ||
          loadingStatutes ||
          loadingCmsDocs ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
            </Box>
          ) : hasData ? (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  border: "1px solid #F1F5F9",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                }}
              >
                <TableContainer
                  sx={{
                    maxHeight: "600px",
                    overflowY: "auto",
                    overflowX: "auto",
                    "::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "::-webkit-scrollbar-track": {
                      background: "#F8FAFC",
                    },
                    "::-webkit-scrollbar-thumb": {
                      background: "#CBD5E1",
                      borderRadius: "8px",
                      "&:hover": {
                        background: "#94A3B8",
                      },
                    },
                    "::-webkit-scrollbar:horizontal": {
                      height: "6px",
                    },
                    "::-webkit-scrollbar-thumb:horizontal": {
                      background: "#CBD5E1",
                      borderRadius: "8px",
                      "&:hover": {
                        background: "#94A3B8",
                      },
                    },
                  }}
                >
                  <Table
                    stickyHeader
                    sx={{ minWidth: 650, marginBottom: "10px" }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#F8FAFC",
                          borderBottom: "1px solid #E2E8F0",
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#12243C",
                            padding: "16px 24px",
                            fontSize: "14px",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            borderBottom: "none",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#12243C",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              Title
                            </Typography>
                            <IconButton
                              onClick={() => handleSort("title")}
                              size="small"
                              sx={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "6px",
                                color: "#64748B",
                                "&:hover": {
                                  backgroundColor: "#F1F5F9",
                                  color: "#0073A6",
                                },
                              }}
                            >
                              {sortConfig.key === "title" &&
                              sortConfig.direction === "asc" ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#12243C",
                            padding: "16px 24px",
                            fontSize: "14px",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            borderBottom: "none",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          Category
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#12243C",
                            padding: "16px 24px",
                            fontSize: "14px",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            borderBottom: "none",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#12243C",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              Date
                            </Typography>
                            <IconButton
                              onClick={() => handleSort("date")}
                              size="small"
                              sx={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "6px",
                                color: "#64748B",
                                "&:hover": {
                                  backgroundColor: "#F1F5F9",
                                  color: "#0073A6",
                                },
                              }}
                            >
                              {sortConfig.key === "date" &&
                              sortConfig.direction === "asc" ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#12243C",
                            padding: "16px 24px",
                            fontSize: "14px",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            borderBottom: "none",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          Notes
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#12243C",
                            padding: "16px 24px",
                            fontSize: "14px",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            borderBottom: "none",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedAndPaginatedResult &&
                      sortedAndPaginatedResult.length > 0 ? (
                        sortedAndPaginatedResult.map((item, index) => (
                          <TableRow
                            key={item.id}
                            key={`${item.Bookmark_ID}-${index}`}
                            sx={{
                              backgroundColor: "#FFFFFF",
                              transition: "all 0.2s ease-in-out",
                              borderBottom: "1px solid #F1F5F9",
                              "&:hover": {
                                backgroundColor: "#F8FAFC",
                                transform: "translateY(-1px)",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                              },
                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                maxWidth: 400,
                                padding: "16px 24px",
                                fontSize: "14px",
                                color: "#12243C",
                                fontWeight: "500",
                                cursor: "pointer",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                "&:hover": {
                                  color: "#0073A6",
                                },
                              }}
                              onClick={() => handlePreviewClick(item)}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "8px",
                                    backgroundColor:
                                      item.type === "case"
                                        ? "#FEF3C7"
                                        : item.type === "cmsDoc"
                                          ? "#EDE9FE"
                                          : "#DCFCE7",
                                    color:
                                      item.type === "case"
                                        ? "#D97706"
                                        : item.type === "cmsDoc"
                                          ? "#6D28D9"
                                          : "#16A34A",
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.type === "case" ? (
                                    <Gavel size={16} />
                                  ) : item.type === "cmsDoc" ? (
                                    <FileText size={16} />
                                  ) : (
                                    <Scale size={16} />
                                  )}
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "inherit",
                                    fontFamily:
                                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.type === "case"
                                    ? formatCaseTitle(item.Title)
                                    : item.type === "cmsDoc"
                                      ? item.title || "Untitled Document"
                                      : item.type === "statute"
                                        ? formatCaseTitle(item.Statute_Name)
                                        : formatCaseTitle(
                                            `${item.Statute_Name}, Section:${item.Section}`,
                                          )}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "16px 24px",
                                fontSize: "14px",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              <Chip
                                label={
                                  item.type === "case"
                                    ? "Case"
                                    : item.type === "cmsDoc"
                                      ? "CMS Doc"
                                      : "Statute"
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    item.type === "case"
                                      ? "#FEF3C7"
                                      : item.type === "cmsDoc"
                                        ? "#EDE9FE"
                                        : "#DCFCE7",
                                  color:
                                    item.type === "case"
                                      ? "#D97706"
                                      : item.type === "cmsDoc"
                                        ? "#6D28D9"
                                        : "#16A34A",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  borderRadius: "8px",
                                  fontFamily:
                                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                  "& .MuiChip-label": {
                                    padding: "4px 8px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "16px 24px",
                                fontSize: "14px",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#64748B",
                                }}
                              >
                                <Calendar size={16} />
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#64748B",
                                    fontFamily:
                                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                  }}
                                >
                                  {moment(item.addedAt).isValid()
                                    ? moment(item.addedAt).format(
                                        "MMM DD, YYYY",
                                      )
                                    : "N/A"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "16px 24px",
                                fontSize: "14px",
                                maxWidth: "200px",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  cursor: "pointer",
                                  padding: "8px 12px",
                                  borderRadius: "8px",
                                  backgroundColor: item.note
                                    ? "#F0F9FF"
                                    : "#F8FAFC",
                                  border: item.note
                                    ? "1px solid #BFDBFE"
                                    : "1px solid #E2E8F0",
                                  transition: "all 0.2s ease-in-out",
                                  "&:hover": {
                                    backgroundColor: item.note
                                      ? "#E0F2FE"
                                      : "#F1F5F9",
                                    borderColor: "#0073A6",
                                  },
                                }}
                                onClick={() => handleNoteClick(item)}
                              >
                                <Edit3
                                  size={16}
                                  color={item.note ? "#0073A6" : "#64748B"}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: item.note ? "#12243C" : "#64748B",
                                    fontFamily:
                                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {item.note || "Add a note..."}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "16px 24px",
                                fontSize: "14px",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              <Box sx={{ display: "flex", gap: "8px" }}>
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "8px",
                                      backgroundColor: "#F0F9FF",
                                      color: "#0073A6",
                                      "&:hover": {
                                        backgroundColor: "#E0F2FE",
                                        transform: "scale(1.05)",
                                      },
                                      transition: "all 0.2s ease-in-out",
                                    }}
                                    onClick={() => handlePreviewClick(item)}
                                  >
                                    <Eye size={16} />
                                  </IconButton>
                                </Tooltip>

                                {item.type !== "cmsDoc" && (
                                  <Tooltip title="Copy to another group">
                                    <IconButton
                                      size="small"
                                      sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "8px",
                                        backgroundColor: "#FEF3C7",
                                        color: "#D97706",
                                        "&:hover": {
                                          backgroundColor: "#FDE68A",
                                          transform: "scale(1.05)",
                                        },
                                        transition: "all 0.2s ease-in-out",
                                      }}
                                      onClick={() => handleMove(item)}
                                    >
                                      <Copy size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {/* <Tooltip title="Edit note">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "8px",
                                      backgroundColor: "#F0FDF4",
                                      color: "#16A34A",
                                      "&:hover": {
                                        backgroundColor: "#DCFCE7",
                                        transform: "scale(1.05)",
                                      },
                                      transition: "all 0.2s ease-in-out",
                                    }}
                                    onClick={() => handleNoteClick(item)}
                                  >
                                    <Edit3 size={16} />
                                  </IconButton>
                                </Tooltip> */}

                                <Tooltip title="More options">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "8px",
                                      backgroundColor: "#FEF2F2",
                                      color: "#DC2626",
                                      "&:hover": {
                                        backgroundColor: "#FEE2E2",
                                        transform: "scale(1.05)",
                                      },
                                      transition: "all 0.2s ease-in-out",
                                    }}
                                    onClick={(event) => {
                                      setSelectItem(item);
                                      setAnchorEl(event.currentTarget);
                                    }}
                                  >
                                    <MoreHorizontal size={16} />
                                  </IconButton>
                                </Tooltip>

                                <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl)}
                                  onClose={() => setAnchorEl(null)}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                  PaperProps={{
                                    sx: {
                                      borderRadius: "12px",
                                      boxShadow:
                                        "0 10px 25px rgba(0, 0, 0, 0.1)",
                                      border: "1px solid #E2E8F0",
                                      minWidth: "180px",
                                      mt: 1,
                                    },
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      handlePreviewClick(selectItem);
                                      setAnchorEl(null);
                                    }}
                                    sx={{
                                      fontSize: "14px",
                                      fontFamily:
                                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                      color: "#475569",
                                      padding: "8px 16px",
                                      "&:hover": {
                                        backgroundColor: "#F8FAFC",
                                      },
                                    }}
                                  >
                                    <ListItemIcon>
                                      <Eye
                                        size={16}
                                        style={{ color: "#0073A6" }}
                                      />
                                    </ListItemIcon>
                                    <Typography sx={{ fontSize: "14px" }}>
                                      Preview
                                    </Typography>
                                  </MenuItem>
                                  {selectItem.type === "case" && (
                                    <>
                                      <MenuItem
                                        onClick={() => {
                                          let creditsToDeduct = 1;
                                          if (
                                            credits <= 0 ||
                                            credits <= creditsToDeduct
                                          ) {
                                            dispatch(
                                              showInsufficientCreditsModal(),
                                            ); // Trigger modal if credits are insufficient
                                            return;
                                          }
                                          dispatch({
                                            type: "CASE_DETAIL_SUCCESS",
                                            payload: selectItem,
                                          });

                                          setOpenCaseInsights(true);
                                          setAnchorEl(null);
                                          const deductionSuccess =
                                            deductCredits(
                                              {
                                                userId,
                                                creditsToDeduct: 1,
                                                event:
                                                  "Open case insights in Bookmark",
                                              },
                                              dispatch,
                                            );

                                          if (!deductionSuccess) {
                                            console.warn(
                                              "Failed to deduct credits. Ensure retry or user notification.",
                                            );
                                          }
                                        }}
                                        sx={{
                                          fontSize: "14px",
                                          fontFamily:
                                            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                          color: "#475569",
                                          padding: "8px 16px",
                                          "&:hover": {
                                            backgroundColor: "#F8FAFC",
                                          },
                                        }}
                                      >
                                        <ListItemIcon>
                                          <BarChart3
                                            size={16}
                                            style={{ color: "#10B981" }}
                                          />
                                        </ListItemIcon>
                                        <Typography sx={{ fontSize: "14px" }}>
                                          Case Insights
                                        </Typography>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() => {
                                          let creditsToDeduct = 1;
                                          if (
                                            credits <= 0 ||
                                            credits <= creditsToDeduct
                                          ) {
                                            dispatch(
                                              showInsufficientCreditsModal(),
                                            ); // Trigger modal if credits are insufficient
                                            return;
                                          }
                                          handleSummaryClick(selectItem);
                                          setAnchorEl(null);
                                          const deductionSuccess =
                                            deductCredits(
                                              {
                                                userId,
                                                creditsToDeduct: 1,
                                                event:
                                                  "Open summary of Bookmarked item",
                                              },
                                              dispatch,
                                            );

                                          if (!deductionSuccess) {
                                            console.warn(
                                              "Failed to deduct credits. Ensure retry or user notification.",
                                            );
                                          }
                                        }}
                                        sx={{
                                          fontSize: "14px",
                                          fontFamily:
                                            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                          color: "#475569",
                                          padding: "8px 16px",
                                          "&:hover": {
                                            backgroundColor: "#F8FAFC",
                                          },
                                        }}
                                      >
                                        <ListItemIcon>
                                          <FontAwesomeIcon
                                            icon={faRectangleList}
                                            color="#ff9800"
                                            fontSize="16px"
                                          />
                                        </ListItemIcon>
                                        <Typography sx={{ fontSize: "14px" }}>
                                          Summary
                                        </Typography>
                                      </MenuItem>
                                    </>
                                  )}
                                  {selectItem.type !== "cmsDoc" && (
                                    <MenuItem
                                      onClick={() => {
                                        handleMove(selectItem);
                                        setAnchorEl(null);
                                      }}
                                      sx={{
                                        fontSize: "14px",
                                        fontFamily:
                                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                        color: "#475569",
                                        padding: "8px 16px",
                                        "&:hover": {
                                          backgroundColor: "#F8FAFC",
                                        },
                                      }}
                                    >
                                      <ListItemIcon>
                                        <ArrowRightLeft
                                          size={16}
                                          style={{ color: "#10B981" }}
                                        />
                                      </ListItemIcon>
                                      <Typography sx={{ fontSize: "14px" }}>
                                        Add to Another Group
                                      </Typography>
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    onClick={() => {
                                      console.log("testing: ", selectItem);
                                      if (selectItem.type === "cmsDoc") {
                                        const link = `${window.location.origin}/app/cms-document/${selectItem.caseId}/${selectItem._id}`;
                                        navigator.clipboard.writeText(link);
                                      } else if (selectItem.type === "case") {
                                        const link = `${window.location.origin}/app/searchResults/${selectItem.Bookmark_ID}`;
                                        navigator.clipboard.writeText(link);
                                      } else {
                                        const link = `${window.location.origin}/app/statute-search/${encodeURIComponent(selectItem?.Statute_Name)}`;
                                        navigator.clipboard.writeText(link);
                                      }
                                      toast.success("Link copied to clipboard");
                                      setAnchorEl(null);
                                    }}
                                    sx={{
                                      fontSize: "14px",
                                      fontFamily:
                                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                      color: "#475569",
                                      padding: "8px 16px",
                                      "&:hover": {
                                        backgroundColor: "#F8FAFC",
                                      },
                                    }}
                                  >
                                    <ListItemIcon>
                                      <Link2
                                        size={16}
                                        style={{ color: "#607d8b" }}
                                      />
                                    </ListItemIcon>
                                    <Typography sx={{ fontSize: "14px" }}>
                                      Copy Link
                                    </Typography>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      handleOpenConfirmDialog(
                                        selectItem.Bookmark_ID,
                                        selectedGroup?.id,
                                        selectItem.type,
                                      );
                                      setAnchorEl(null);
                                    }}
                                    sx={{
                                      fontSize: "14px",
                                      fontFamily:
                                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                      color: "#DC2626",
                                      padding: "8px 16px",
                                      "&:hover": {
                                        backgroundColor: "#FEF2F2",
                                      },
                                    }}
                                  >
                                    <ListItemIcon>
                                      <Trash2
                                        size={16}
                                        style={{ color: "#DC2626" }}
                                      />
                                    </ListItemIcon>
                                    <Typography sx={{ fontSize: "14px" }}>
                                      Delete
                                    </Typography>
                                  </MenuItem>
                                </Menu>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{
                              textAlign: "center",
                              padding: "60px 24px",
                              fontSize: "14px",
                              color: "#64748B",
                              fontFamily:
                                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "64px",
                                  height: "64px",
                                  borderRadius: "16px",
                                  backgroundColor: "#F8FAFC",
                                  color: "#64748B",
                                }}
                              >
                                <Bookmark size={32} />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "18px",
                                  fontWeight: "600",
                                  color: "#12243C",
                                  fontFamily:
                                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                }}
                              >
                                No bookmarks found
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#64748B",
                                  fontFamily:
                                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                  textAlign: "center",
                                  maxWidth: "400px",
                                }}
                              >
                                Start adding bookmarks to this group to organize
                                your legal references
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableContainer>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // marginTop: "24px",
                  width: "100%",
                  // padding: "16px 0",
                  // borderTop: "1px solid #F1F5F9",
                  "& .MuiTablePagination-root": {
                    width: "100%",
                    maxWidth: { xs: "100%", sm: "80%", md: "60%" },
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                  "& .MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    justifyContent: "center",
                    // gap: "16px",
                  },
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    color: "#12243C",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                  "& .MuiTablePagination-actions": {
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#64748B",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    fontSize: "14px",
                    color: "#64748B",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                }}
              >
                <TablePagination
                  component="div"
                  count={sortedItems.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "80px",
                  height: "80px",
                  borderRadius: "20px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                  marginBottom: "24px",
                }}
              >
                <Bookmark size={40} />
              </Box>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#12243C",
                  marginBottom: "12px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  textAlign: "center",
                }}
              >
                No Bookmarks Found
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#64748B",
                  textAlign: "center",
                  maxWidth: "500px",
                  lineHeight: "1.5",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Start organizing your key legal references for quick and easy
                access. Create bookmark groups and add cases and statutes to
                keep them organized.
              </Typography>
            </Box>
          )}
        </>
        {snackbarMessage && (
          <Snackbar
            open={!!snackbarMessage}
            autoHideDuration={6000}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            onClose={() => setSnackbarMessage(null)}
          >
            <Alert
              onClose={() => setSnackbarMessage(null)}
              severity={snackbarMessage.type}
              sx={{ width: "100%" }}
            >
              {snackbarMessage.text}
            </Alert>
          </Snackbar>
        )}
      </Box>
      {openSummary && (
        <CaseSummaryDialog
          open={openSummary}
          onClose={() => setOpenSummary(false)}
          title={selectedCase ? formatCaseTitle(selectedCase?.Title || "") : ""}
          summary={selectedCase?.Detailed_Lawcase_Summary}
        />
      )}

      <CaseDetailDialog
        open={openCaseInsights}
        onClose={() => setOpenCaseInsights(false)}
      />
      <MoveBookmarkDialog
        open={moveModalOpen}
        onClose={handleMoveCancel}
        onConfirm={handleMoveConfirm}
        groups={groups}
        currentGroupId={selectedGroup?.id}
        targetGroupId={targetGroupId}
        onTargetGroupChange={handleSelectGroupToCopy}
        bookmarkAlreadyExists={bookmarkAlreadyExists}
      />
      {selectedCase && (
        <CaseLawPreviewDialog
          open={openPreview}
          onClose={handleClosePreview}
          title={selectedCase ? formatCaseTitle(selectedCase.Title) : ""}
          court={selectedCase ? selectedCase.Court : ""}
          citation={selectedCase ? selectedCase.Citation_Name : ""}
          judge={selectedCase ? selectedCase.judge_name_clean : ""}
          caseDescriptionContent={
            selectedCase ? selectedCase.Case_Description_HTML : ""
          }
        />
      )}
      {selectedStatute && (
        <StatuteDetailDialog open={openPreview} onClose={handleClosePreview} />
        // <StatutePreviewModal
        //   open={openPreview}
        //   onClose={handleClosePreview}
        //   statute={selectedStatute}
        // />
      )}
      {selectedStatute && (
        <StatutePreviewDialog
          open={openStatuteSectionPreview}
          onClose={() => setOpenStatuteSectionPreview(false)}
          statute={selectedStatute}
        />
      )}

      <EditNoteDialog
        open={!!editNoteItem}
        onClose={() => setEditNoteItem(null)}
        initialNote={editNoteItem?.note || ""}
        onSave={handleSaveNote}
        onDelete={handleRemoveNoteClick}
      />

      <ConfirmationDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={() => {
          handleDelete(
            itemToDelete.itemId,
            itemToDelete.groupId,
            itemToDelete.itemType,
          );
          handleCloseConfirmDialog();
        }}
        title="Confirm Deletion"
        contentText="Are you sure you want to delete this bookmark? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

export default BookmarksTable;
